import { call, getContext, put, race, take, takeLeading } from 'redux-saga/effects';
import { jwtDecode } from 'jwt-decode';
import { DeeplinkApi } from 'api';
import { AuthActions, UserActions } from 'store/actions';
import { GetAction } from 'store/types';
import { apiCall } from 'store/utils';
import { createSessionUser, createSessionUserFromAccess } from 'store/features/auth/utils/sessionUser';
import { Access } from 'store/features/auth/types';
import { serializeAuthCookie } from 'store/features/auth/utils/sessionCookie';
import { setSerializedCookie } from 'utils/cookie';
import * as actions from './actions';

function* applyDeeplinkAuth(access: string, refresh: string): any {
  const next: any = yield getContext('next');
  setSerializedCookie(next, serializeAuthCookie({ access, refresh }));
  const decoded = jwtDecode<Access>(access);
  const userId = Number(decoded.user_id);
  yield put(AuthActions.setSessionUser(createSessionUserFromAccess(decoded)));

  // Fetch the full user profile and WAIT before returning so that isHardUser /
  // currentUser are hydrated before deeplinkEntry.success fires and the hook
  // navigates. Without this, auth gates (withAuth on /user/orders, useAuthFlow on
  // the home page) read isHardUser=false for a freshly auto-logged-in hard user and
  // wrongly show the Create Account modal (JPA-8148). Mirrors handleLoginMagicLinkRequest
  // (auth/sagas.ts:126-138).
  const createActionPattern = (type: string) => (a: any) => a.type === type && a.payload.entity?.id === userId;
  yield put(UserActions.getUserDetail.request({ id: userId, silent: true }));
  const effects: any[] = yield race([
    take(createActionPattern(UserActions.getUserDetail.success.type)),
    take(UserActions.getUserDetail.failure.type),
  ]);
  const [success] = effects;
  if (success) {
    yield put(AuthActions.setSessionUser(createSessionUser(success.payload.entity)));
  }
}

function* handleDeeplinkEntry(action: GetAction<typeof actions.deeplinkEntry.request>): any {
  try {
    const result = yield apiCall(DeeplinkApi.entry, { body: action.payload.params });
    if (result.access && result.refresh && result.status === 'NO_SESSION') {
      yield call(applyDeeplinkAuth, result.access, result.refresh);
    }
    yield put(actions.deeplinkEntry.success({ result }));
  } catch (error) {
    if (error instanceof Error) {
      yield put(actions.deeplinkEntry.failure({ error }));
    }
  }
}

function* handleDeeplinkLinkAccount(action: GetAction<typeof actions.deeplinkLinkAccount.request>): any {
  try {
    const result = yield apiCall(DeeplinkApi.linkAccount, { body: { ...action.payload.params, allow_relink: false } });
    yield put(actions.deeplinkLinkAccount.success({ result }));
  } catch (error) {
    if (error instanceof Error) {
      yield put(actions.deeplinkLinkAccount.failure({ error }));
    }
  }
}

function* handleDeeplinkConflictResolve(action: GetAction<typeof actions.deeplinkConflictResolve.request>): any {
  try {
    const result = yield apiCall(DeeplinkApi.conflictResolve, { body: action.payload.params });
    if (result.access && result.refresh && result.status === 'switched') {
      yield call(applyDeeplinkAuth, result.access, result.refresh);
    }
    yield put(actions.deeplinkConflictResolve.success({ result }));
  } catch (error) {
    if (error instanceof Error) {
      yield put(actions.deeplinkConflictResolve.failure({ error }));
    }
  }
}

export default function* deeplinkSagas() {
  yield takeLeading(actions.deeplinkEntry.request.type, handleDeeplinkEntry);
  yield takeLeading(actions.deeplinkLinkAccount.request.type, handleDeeplinkLinkAccount);
  yield takeLeading(actions.deeplinkConflictResolve.request.type, handleDeeplinkConflictResolve);
}
